import { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import { Tooltip, Typography } from "@mui/material";

import { KALNIRNAY_CUSTOMERS_EXPORT_ENDPOINT, KALNIRNAY_CUSTOMERS_LIST_ENDPOINT } from "../../config/urls";
import { useAuth } from "../../hooks/AuthProvider";
import { Download } from "@mui/icons-material";
import { ColorsConstants } from "../../cssUtils/constants";
import typography from "../../cssUtils/typography";
import {
  resolveSearchQueryForApi,
  normalizeCombinedSearchInput,
  PHONE_SEARCH_ERROR_MESSAGE,
} from "../../utils/phoneSearch";
import ButtonOne from "../../common/normal-button";
import AppTable from "../../common/app-table";
import TableSearchInput from "../TableSearchInput";

const columns = [
  { id: "name", label: "Name", align: "left" },
  { id: "phone_no", label: "Phone Number", align: "left" },
  { id: "address", label: "Address", align: "left" },
  { id: "total_orders_placed", label: "Total Orders Placed", align: "left" },
  { id: "total_spent", label: "Total Spent", align: "left" },
  { id: "last_successful_order", label: "Last Successful Order", align: "left" },
];

export default function KalnirnayCustomers({ refreshKey, onNavigate }) {
  const { getValidToken } = useAuth();
  const [rowCount, setRowCount] = useState(0);
  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: 20,
  });

  const [orders, setorders] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [charCount, setCharCount] = useState(0);

  const resolvedKalnirnaySearch = useMemo(
    () => resolveSearchQueryForApi(searchQuery),
    [searchQuery],
  );

  const fetchData = async () => {
    if (resolvedKalnirnaySearch.blockFetch) {
      setorders([]);
      setRowCount(0);
      return;
    }
    let url = KALNIRNAY_CUSTOMERS_LIST_ENDPOINT;

    let queryParams = new URLSearchParams({
      limit: controller.rowsPerPage.toString(),
      offset: (controller.page * controller.rowsPerPage).toString(),
      search: resolvedKalnirnaySearch.apiSearchQuery,
    });

    url += `?${queryParams}`;

    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }
      const jsonData = await response.json();

      setorders(jsonData.results);
      setRowCount(jsonData.count);
    } catch (error) {
      console.error("Error fetching data:", error.message);
    }
  };

  useEffect(() => {
    fetchData();
  }, [controller.page, controller.rowsPerPage, refreshKey, searchQuery]);

  const handlePageChange = (event, newPage) => {
    setController((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const handleChangeRowsPerPage = (event) => {
    setController({
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0,
    });
  };

  const updateSearchValue = (value) => {
    const normalized = normalizeCombinedSearchInput(value);
    if (normalized.length > 50) {
      return;
    }
    setSearchQuery(normalized);
    setCharCount(normalized.length);
    setController({
      page: 0,
      rowsPerPage: 20,
    });
  };

  const handleExport = async () => {
    const exportSearch = resolveSearchQueryForApi(searchQuery);
    if (exportSearch.blockFetch) {
      alert(PHONE_SEARCH_ERROR_MESSAGE);
      return;
    }
    let url = KALNIRNAY_CUSTOMERS_EXPORT_ENDPOINT;

    let queryParams = new URLSearchParams({
      limit: controller.rowsPerPage.toString(),
      offset: (controller.page * controller.rowsPerPage).toString(),
      search: exportSearch.apiSearchQuery,
    });

    url += `?${queryParams}`;

    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        },
      });

      if (!response.ok) {
        try {
          const error = (await response.json()).error;
          alert(error);
          throw new Error(error);
        } catch {
          throw new Error("Failed to export data");
        }
      }

      const responseClone = response.clone();
      const blob = await responseClone.blob();

      const urlBlob = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = urlBlob;

      const today = new Date();
      const dateStr = today.toISOString().split("T")[0];
      a.download = `customers-${dateStr}.csv`;

      document.body.appendChild(a);
      a.click();

      window.URL.revokeObjectURL(urlBlob);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error exporting data:", error.message);
      throw error;
    }
  };

  const tableColumns = columns.map((column) => ({
    id: column.id,
    label: column.label,
    align: column.align,
    render: (row) => {
      if (column.id === "name") {
        return (
          <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
            {row.name || "N/A"}
          </Typography>
        );
      }

      if (column.id === "phone_no") {
        return (
          <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
            {row.phone_display || "N/A"}
          </Typography>
        );
      }

      if (column.id === "address") {
        return (
          <Tooltip title={row.address || ""} placement="top-start" arrow>
            <Typography
              sx={{
                ...typography.bodyLarge,
                maxWidth: 250,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                color: "#1D2330",
              }}
            >
              {row.address || "N/A"}
            </Typography>
          </Tooltip>
        );
      }

      if (column.id === "total_orders_placed") {
        return (
          <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
            {row.total_orders_placed || 0}
          </Typography>
        );
      }

      if (column.id === "total_spent") {
        return (
          <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
            {row.total_spend ? `₹${row.total_spend}` : "N/A"}
          </Typography>
        );
      }

      if (column.id === "last_successful_order") {
        return (
          <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
            {row.last_successful_order || "N/A"}
          </Typography>
        );
      }

      return <Typography sx={typography.bodyLarge}>N/A</Typography>;
    },
  }));

  const rowSx = (row, index) => ({
    backgroundColor: index % 2 === 0 ? "#F0F5FF" : "#FFFFFF",
    "&:hover": {
      backgroundColor: index % 2 === 0 ? "#F5F5F5" : "#F7F9FC",
    },
    "& td": {
      borderBottom: "none",
      borderTop: "none",
    },
  });

  return (
    <Box sx={{ p: 3, bgcolor: "#fafafa" }}>
      {/* Header Section with Search and Export Button */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
          width: "100%",
        }}
      >
        {/* Search - Takes remaining width */}
        <Box sx={{ flex: 1 }}>
          <TableSearchInput
            searchQuery={searchQuery}
            onSearchChange={(e) => updateSearchValue(e.target.value)}
            charCount={charCount}
            tooltip="Search by Name or Phone Number"
            placeholder="Search by name or phone number..."
            error={Boolean(resolvedKalnirnaySearch.blockFetch && resolvedKalnirnaySearch.phoneSearchError)}
            helperText={resolvedKalnirnaySearch.blockFetch ? resolvedKalnirnaySearch.phoneSearchError : undefined}
          />
        </Box>

        {/* Export Button - Fixed width */}
        <Box sx={{ flex: "0 0 auto" }}>
          <ButtonOne
            variant="contained"
            onClick={handleExport}
            startIcon={<Download sx={{ fontSize: 18 }} />}
            sx={{
              ...typography.bodyLarge,
              backgroundColor: ColorsConstants.primaryPurple100,
              whiteSpace: "nowrap",
              "&:hover": {
                backgroundColor: ColorsConstants.primaryPurple100,
                opacity: 0.9,
              },
            }}
          >
            Export
          </ButtonOne>
        </Box>
      </Box>

      {/* Table using AppTable */}
      <AppTable
        columns={tableColumns}
        rows={orders}
        rowKey={(row, index) => index}
        maxHeight={540}
        containerSx={{
          scrollbarWidth: "thin",
          scrollbarColor: "#C5CEDB #F3F6FB",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#F3F6FB",
            borderRadius: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#C5CEDB",
            borderRadius: "8px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#A8B4C7",
          },
        }}
        rowSx={rowSx}
        paperSx={{
          border: "1px solid #DCDFE5",
          borderRadius: "12px",
          mb: 2,
        }}
        emptyMessage="No customers found"
        count={rowCount}
        page={controller.page}
        rowsPerPage={controller.rowsPerPage}
        rowsPerPageOptions={[20, 50, 100]}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
}
